import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useStateInRef, useIsMounted, FunctionalLoader } from 'seagull';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { useIoContext, useStore } from '~/contexts';
export var RestfulStrategy = /*#__PURE__*/ function(RestfulStrategy) {
    RestfulStrategy["Default"] = "default";
    RestfulStrategy["PreferLoading"] = "preferLoading";
    return RestfulStrategy;
}({});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useRestfulWrapper(options) {
    const { initialData, getStoreLocation, initialArgs } = options;
    const getStateInRef = useStateInRef(options);
    const getRefresh = useStateInRef(refresh);
    const { getNow } = useIoContext();
    const isMounted = useIsMounted();
    const storeState = useStore((state)=>getStoreLocation ? getStoreLocation(state) : undefined);
    const [{ _loading, _error, _data, _lastSuccessfulFetch }, setState] = useState({
        _loading: false,
        _error: undefined,
        _data: initialData || (storeState ? storeState.data : undefined),
        _lastSuccessfulFetch: undefined
    });
    const { data, error, loading, lastSuccessfulFetch } = storeState || {
        data: _data,
        error: _error,
        loading: _loading,
        lastSuccessfulFetch: _lastSuccessfulFetch
    };
    useEffect(()=>{
        refresh({
            args: initialArgs
        }).catch(()=>{});
    }, []);
    async function refresh() {
        let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        const { silent, args } = options;
        Sentry.addBreadcrumb({
            type: 'refresh',
            category: 'fetch',
            data: {
                silent
            }
        });
        try {
            !silent && setState((prev)=>_object_spread_props(_object_spread({}, prev), {
                    _loading: true
                }));
            const res = await getStateInRef().fetch(args);
            const mounted = isMounted();
            if (mounted) {
                setState((prev)=>_object_spread_props(_object_spread({}, prev), {
                        _error: null,
                        _data: res,
                        _lastSuccessfulFetch: getNow().getTime(),
                        _loading: silent ? prev._loading : false
                    }));
            }
            Sentry.addBreadcrumb({
                type: 'success',
                category: 'fetch',
                data: {
                    mounted
                }
            });
            return res;
        } catch (err) {
            const mounted = isMounted();
            if (mounted) {
                setState((prev)=>_object_spread_props(_object_spread({}, prev), {
                        _error: err,
                        _data: undefined,
                        _loading: silent ? prev._loading : false
                    }));
            }
            Sentry.addBreadcrumb({
                type: 'error',
                category: 'fetch',
                data: {
                    mounted,
                    err
                }
            });
            throw err;
        }
    }
    function renderRestfulState(props) {
        const { strategy = "default", renderChild, renderError, renderLoader } = props;
        function renderErrorState() {
            if (!renderError) {
                throw makeSentryIgnoredError(error);
            }
            return renderError({
                loading,
                error,
                refresh: refresh
            });
        }
        function renderLoadingState() {
            return renderLoader ? renderLoader() : /*#__PURE__*/ _jsx(FunctionalLoader, {
                show: true
            });
        }
        function renderDataState() {
            return renderChild({
                data: data,
                loading,
                refresh: refresh
            });
        }
        switch(strategy){
            case "default":
                return error ? renderErrorState() : data || lastSuccessfulFetch ? renderDataState() : renderLoadingState();
            case "preferLoading":
                return error ? renderErrorState() : loading || !data ? renderLoadingState() : renderDataState();
        }
    }
    return {
        data,
        error,
        loading,
        lastSuccessfulFetch,
        refresh: (options)=>getRefresh()(options),
        renderRestfulState
    };
}
